import { yupResolver } from '@hookform/resolvers/yup'
import { DateOnly } from '@if/core/date'
import { EFinancingProgram, EPaymentFrequency, EWorksheetStatus } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { Paper } from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useUpdateAutoWorksheet } from '@src/data/api/auto-worksheet/auto-worksheet-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useGetVehicleMaxTerm } from '@src/data/api/vehicle/vehicle-api'
import { useBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { FieldError, FieldErrorsImpl, Merge, Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb, PageError } from '../../components'
import {
  AutoWorksheet,
  AutoWorksheetSchema,
  EExtendedServiceDuration,
  EGapInsuranceDuration,
  Vehicle,
} from '../../data/types'
import { reportErrorToConsole } from '../../services/error-logger'
import { useFirstPaymentDateOptions } from '../EditWorksheet/editWorksheet-hooks'
import TransactionForm from './components/TransactionForm'
import VehicleForm from './components/VehicleForm'
import ProgramSectionEdit from './components/programSection'

const EditWorksheetPage = () => {
  const { t } = useTranslation()

  // Get worksheet from context
  const { creditApplication, worksheet: baseWorksheet } = useCreditApplicationData()
  const worksheet = baseWorksheet as AutoWorksheet | undefined

  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const error = useBusinessErrors()
  const navigate = useNavigate()
  const [updateAutoWorksheet] = useUpdateAutoWorksheet()

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
    {
      path: `/Applications/${EFinancingProgram.Auto}/${creditApplication?.id}/view`,
      label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
    },
    { path: '#', label: t('worksheet.financing') },
  ]

  const editDisabled = creditApplication?.editLocked === true
  const today = formatDate(new Date())

  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    setValue,
    setError,
    formState: { errors },
    reset,
  } = useForm<AutoWorksheet>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: {
      ...worksheet,
      deliveryOn: worksheet?.deliveryOn
        ? formatDate(worksheet.deliveryOn) < today
          ? today.toString()
          : worksheet.deliveryOn
        : today.toString(), // Fallback to today if deliveryOn is undefined
    },
    // Forcing type to be backward compatible with the old schema (should be removed in the future)
    resolver: yupResolver(AutoWorksheetSchema) as unknown as Resolver<AutoWorksheet, unknown, AutoWorksheet>,
  })

  const vehicle = watch('vehicle')
  const deliveryOn = watch('deliveryOn')
  const paymentFrequency = watch('paymentFrequency')

  const [vehicleMaxTerm] = useGetVehicleMaxTerm(
    creditApplication && vehicle
      ? {
          year: Number(vehicle.year),
          mileage: Number(vehicle.mileage),
          plan: creditApplication.normsCreditDecision?.planName,
        }
      : null,
  )

  const addFrequencyToDate = (date: string, frequency: number) => {
    return new Date(new Date(date).setDate(new Date(date).getUTCDate() + frequency))
  }

  const verifyFirstPaymentDate = (data: AutoWorksheet) => {
    const frequencyMonthlyMaxDate = addFrequencyToDate(data.deliveryOn, 30)
    const frequencyBiWeeklyMaxDate = addFrequencyToDate(data.deliveryOn, 14)
    const firstPaymentOnDate = new Date(data.firstPaymentOn)
    const deliveryDate = addFrequencyToDate(data.deliveryOn, 0)

    if (firstPaymentOnDate <= deliveryDate) {
      setError('firstPaymentOn', { message: t('worksheet.norms.firstPaymentDateMustBeHigherThanDeliveryDate') })
      return false
    }

    if (data.paymentFrequency === EPaymentFrequency.BiWeekly) {
      if (firstPaymentOnDate > frequencyBiWeeklyMaxDate) {
        setError('firstPaymentOn', {
          message: t('worksheet.norms.firstPaymentDateIsHigherThanMaxFirstPaymentOn', '', {
            val: frequencyBiWeeklyMaxDate.toLocaleDateString(),
          }),
        })
        return false
      }
    } else if (firstPaymentOnDate > frequencyMonthlyMaxDate) {
      setError('firstPaymentOn', {
        message: t('worksheet.norms.firstPaymentDateIsHigherThanMaxFirstPaymentOn', '', {
          val: frequencyMonthlyMaxDate.toLocaleDateString(),
        }),
      })
      return false
    }

    return true
  }

  const verifyTerm = (data: AutoWorksheet) => {
    const maxTerm = creditApplication?.finalCreditDecision.maxTermDuration
    if (maxTerm && data.term > maxTerm) {
      setError('term', { message: t('worksheet.paramNames.maxTermDuration', '', { val: maxTerm.toString() }) })
      return false
    }

    return true
  }

  const verifyGapDuration = (data: AutoWorksheet) => {
    if (data.gapInsurance && data.gapInsuranceDuration === EGapInsuranceDuration.unknown) {
      setError('gapInsuranceDuration', {
        message: t('worksheet.gapInsuranceDurationIsRequired'),
      })
      return false
    }
    if (
      !data.gapInsurance &&
      data.gapInsuranceDuration !== EGapInsuranceDuration.unknown &&
      data.gapInsuranceDuration !== null
    ) {
      setError('gapInsurance', {
        message: t('worksheet.gapInsuranceIsRequired'),
      })
      return false
    }
    if (
      data.gapInsuranceDuration !== EGapInsuranceDuration.unknown &&
      data.gapInsuranceDuration !== null &&
      (!data.gapInsurance || data.gapInsurance <= 0)
    ) {
      setError('gapInsurance', {
        message: t('mixed.default', { ns: 'validation' }),
      })
      return false
    }

    return true
  }

  const verifyExtendedDuration = (data: AutoWorksheet) => {
    if (
      data.extendedService &&
      data.extendedService > 0 &&
      (data.extendedServiceDuration === EExtendedServiceDuration.unknown || data.extendedServiceDuration === null)
    ) {
      setError('extendedServiceDuration', {
        message: t('worksheet.extendedServiceDurationIsRequired'),
      })
      return false
    }
    if (
      data.extendedService === null &&
      data.extendedServiceDuration !== EExtendedServiceDuration.unknown &&
      data.extendedServiceDuration !== null
    ) {
      setError('extendedService', {
        message: t('worksheet.extendedServiceIsRequired'),
      })
      return false
    }

    if (
      data.extendedServiceDuration !== EExtendedServiceDuration.unknown &&
      data.extendedServiceDuration !== null &&
      (!data.extendedService || data.extendedService <= 0)
    ) {
      setError('extendedService', {
        message: t('mixed.default', { ns: 'validation' }),
      })
      return false
    }
    return true
  }
  const verifyGapInsurance = (data: AutoWorksheet) => {
    if (data.gapInsurance === null && data.gapInsuranceDuration === EGapInsuranceDuration.unknown) {
      return true
    }
    if (data.gapInsuranceDuration !== EGapInsuranceDuration.unknown && data.gapInsurance !== null) {
      if (data.gapInsuranceDuration === EGapInsuranceDuration.lessThanSixtyMonths && data.gapInsurance > 2000) {
        setError('gapInsurance', {
          message: t('worksheet.paramNames.maxGapInsuranceAmountForTermDurationLessThanSixtyMonths', '', {
            val: '2000',
          }),
        })
        return false
      }
      if (data.gapInsuranceDuration === EGapInsuranceDuration.sixtyMonthsOrMore && data.gapInsurance > 2500) {
        setError('gapInsurance', {
          message: t('worksheet.paramNames.maxGapInsuranceAmountForTermDurationSixtyMonthsOrMore', '', { val: '2500' }),
        })
        return false
      }
    }
    return true
  }

  const verifiyExtendedService = (data: AutoWorksheet) => {
    if (data.extendedService !== null && data.extendedServiceDuration !== null) {
      if (data.extendedServiceDuration === EExtendedServiceDuration.twelveMonths && data.extendedService > 1200) {
        setError('extendedService', {
          message: t('worksheet.paramNames.maxExtendedServiceAmountForTwelveMonths', '', { val: '1200' }),
        })
        return false
      }
      if (data.extendedServiceDuration === EExtendedServiceDuration.twentyFourMonths && data.extendedService > 2500) {
        setError('extendedService', {
          message: t('worksheet.paramNames.maxExtendedServiceAmountForTwentyFourMonths', '', { val: '2500' }),
        })
        return false
      }
      if (data.extendedServiceDuration === EExtendedServiceDuration.thirtySixMonth && data.extendedService > 3000) {
        setError('extendedService', {
          message: t('worksheet.paramNames.maxExtendedServiceAmountForThirtySixMonths', '', { val: '3000' }),
        })
        return false
      }
    }

    return true
  }

  const ensureDataAreWithinLimit = (data: AutoWorksheet) => {
    return (
      verifyGapDuration(data) &&
      verifyExtendedDuration(data) &&
      verifyFirstPaymentDate(data) &&
      verifyGapInsurance(data) &&
      verifiyExtendedService(data) &&
      verifyTerm(data)
    )
  }

  const saveWorksheet = async (data: AutoWorksheet) => {
    try {
      await updateAutoWorksheet(data)
      navigate(`/Applications/${EFinancingProgram.Auto}/${creditApplication?.id}/view`, { replace: true })
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const submitWorkSheet = handleSubmit(async (data) => {
    if (ensureDataAreWithinLimit(data)) {
      data.status = EWorksheetStatus.Active
      await saveWorksheet(data)
    }
  }, reportErrorToConsole)

  const saveDraft = async () => {
    const data = getValues()
    data.status = EWorksheetStatus.Draft
    await saveWorksheet(data)
  }

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn: new DateOnly(deliveryOn),
    paymentFrequency,
    isHighEstimate: false,
    setValue,
    getValues,
  })

  return (
    <div>
      <Breadcrumb trees={breadCrumbs} />
      <div style={{ height: '100%' }}>
        <Paper>
          <PageError errors={error} />
          <input type="hidden" value=" " {...register('id')} />
          <ProgramSectionEdit
            worksheet={worksheet!}
            onSubmit={submitWorkSheet}
            onSaveDraft={saveDraft}
            creditApplication={creditApplication!}
            merchant={merchant}
          />

          <VehicleForm
            defaultVehicle={worksheet!.vehicle}
            merchant={merchant}
            control={control}
            getValues={getValues}
            setValue={setValue}
            register={register}
            watch={watch}
            errors={errors.vehicle as Merge<FieldError, FieldErrorsImpl<Vehicle>>}
            name="vehicle"
            title={t('worksheet.vehicle')}
            editDisabled={editDisabled}
          />

          <VehicleForm
            defaultVehicle={worksheet!.tradeInVehicle}
            merchant={merchant}
            control={control}
            getValues={getValues}
            setValue={setValue}
            register={register}
            watch={watch}
            errors={errors.tradeInVehicle as Merge<FieldError, FieldErrorsImpl<Vehicle>>}
            isTradeIn
            name="tradeInVehicle"
            title={t('worksheet.tradeInVehicle')}
            editDisabled={editDisabled}
          />

          <TransactionForm
            control={control}
            currentCreditApplication={creditApplication!}
            applicableTaxOnInsurances={worksheet!.applicableTaxOnInsurances}
            setValue={setValue}
            reset={reset}
            register={register}
            worksheet={worksheet!}
            errors={errors as Merge<FieldError, FieldErrorsImpl<AutoWorksheet>>}
            maxAdmissibleTerm={vehicleMaxTerm}
            creditApplicationInterestRate={creditApplication!.finalCreditDecision.interestRate}
            watch={watch}
            minFirstPaymentDate={minFirstPaymentDate}
            maxFirstPaymentDate={maxFirstPaymentDate}
          />
        </Paper>
      </div>
    </div>
  )
}

export default EditWorksheetPage
